/**
 * Excel parent tools — schemas and Office.js handler implementations.
 *
 * Each tool:
 *  1. Has a `schema` (ParentToolSchema) that is registered with the Usable embed.
 *  2. Has a `handler` that executes the Office.js operation and returns a plain object.
 *
 * All handlers wrap operations in Excel.run() and call context.sync().
 * Read operations use .load() before sync and read values after.
 * Large writes are chunked in ≤10-row batches to avoid Office API timeouts.
 */

import { ParentToolSchema, executeToolCall, LONG_TOOL_TIMEOUT_MS } from "@usable/office-embed";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ExcelToolHandler = (args: Record<string, unknown>) => Promise<unknown>;

export interface ExcelTool {
  schema: ParentToolSchema;
  handler: ExcelToolHandler;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSheet(context: Excel.RequestContext, sheetName?: string): Excel.Worksheet {
  return sheetName
    ? context.workbook.worksheets.getItem(sheetName as string)
    : context.workbook.worksheets.getActiveWorksheet();
}

function chunk<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

const tools: ExcelTool[] = [
  // ─── Workbook / Sheet Info ────────────────────────────────────────────────

  {
    schema: {
      name: "get_workbook_info",
      description:
        "Get high-level information about the current Excel workbook: its name, active sheet, and a full list of all worksheets with their names, IDs, and positions.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const workbook = context.workbook;
        const sheets = workbook.worksheets;
        const active = sheets.getActiveWorksheet();

        workbook.load("name");
        sheets.load("items/name,items/id,items/position");
        active.load("name");

        await context.sync();

        return {
          workbookName: workbook.name,
          activeSheet: active.name,
          sheets: sheets.items.map((s) => ({
            name: s.name,
            id: s.id,
            position: s.position,
          })),
        };
      });
    },
  },

  {
    schema: {
      name: "get_active_sheet",
      description:
        "Get the name and used-range data (values) of the currently active worksheet.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");

        const usedRange = sheet.getUsedRangeOrNullObject();
        usedRange.load("address,values,rowCount,columnCount");

        await context.sync();

        if (usedRange.isNullObject) {
          return { sheetName: sheet.name, isEmpty: true, values: [] };
        }

        return {
          sheetName: sheet.name,
          address: usedRange.address,
          rowCount: usedRange.rowCount,
          columnCount: usedRange.columnCount,
          values: usedRange.values,
        };
      });
    },
  },

  // ─── Read ─────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "read_range",
      description: "Read the values from a specific cell range (e.g. 'A1:C10') on a worksheet.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Cell address or range, e.g. 'A1', 'A1:D10'.",
          },
        },
        required: ["address"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = sheet.getRange(args.address as string);
        range.load("address,values,rowCount,columnCount");
        await context.sync();

        return {
          address: range.address,
          rowCount: range.rowCount,
          columnCount: range.columnCount,
          values: range.values,
        };
      });
    },
  },

  {
    schema: {
      name: "read_selected_range",
      description:
        "Read the values from whatever range the user currently has selected in Excel.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("address,values,rowCount,columnCount");
        await context.sync();

        return {
          address: range.address,
          rowCount: range.rowCount,
          columnCount: range.columnCount,
          values: range.values,
        };
      });
    },
  },

  {
    schema: {
      name: "get_used_range",
      description: "Get all values from the used (non-empty) range of a worksheet.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
        },
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const usedRange = sheet.getUsedRangeOrNullObject();
        usedRange.load("address,values,rowCount,columnCount");
        await context.sync();

        if (usedRange.isNullObject) {
          return { isEmpty: true, values: [] };
        }

        return {
          address: usedRange.address,
          rowCount: usedRange.rowCount,
          columnCount: usedRange.columnCount,
          values: usedRange.values,
        };
      });
    },
  },

  // ─── Write ────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "write_range",
      description:
        "Write a 2-D array of values into a cell range. The values array dimensions must match the address range. Large writes are automatically chunked.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Top-left cell or full range address, e.g. 'A1' or 'A1:C3'.",
          },
          values: {
            type: "array",
            description: "2-D array of values (rows × columns) to write.",
          },
        },
        required: ["address", "values"],
      },
    },
    handler: async (args) => {
      const values = args.values as unknown[][];
      const rowChunks = chunk(values, 10);
      let startRow = 0;

      await Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        // Determine top-left cell from address
        const topLeft = (args.address as string).split(":")[0];

        for (const rowChunk of rowChunks) {
          // Compute the offset address for this chunk
          const chunkAddress = `${topLeft}`;
          const range = sheet.getRange(chunkAddress).getCell(startRow, 0)
            .getResizedRange(rowChunk.length - 1, rowChunk[0].length - 1);
          range.values = rowChunk as string[][];
          await context.sync();
          startRow += rowChunk.length;
        }
      });

      return { success: true, rowsWritten: values.length };
    },
  },

  {
    schema: {
      name: "set_cell_value",
      description: "Write a single value into one cell.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Cell address, e.g. 'A1'.",
          },
          value: {
            description: "Value to write (string, number, or boolean).",
          },
        },
        required: ["address", "value"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const cell = sheet.getRange(args.address as string);
        cell.values = [[args.value]];
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "clear_range",
      description: "Clear the contents (and optionally formatting) of a cell range.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Cell range to clear, e.g. 'A1:C10'.",
          },
          clearType: {
            type: "string",
            enum: ["all", "contents", "formats", "hyperlinks"],
            description: "What to clear. Defaults to 'contents'.",
          },
        },
        required: ["address"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = sheet.getRange(args.address as string);
        const clearType = (args.clearType as Excel.ClearApplyTo) ?? Excel.ClearApplyTo.contents;
        range.clear(clearType);
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Formulas ─────────────────────────────────────────────────────────────

  {
    schema: {
      name: "apply_formula",
      description: "Apply an Excel formula to one or more cells.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Cell or range address.",
          },
          formula: {
            type: "string",
            description: "Excel formula string, e.g. '=SUM(A1:A10)'.",
          },
        },
        required: ["address", "formula"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = sheet.getRange(args.address as string);
        range.formulas = [[args.formula as string]];
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Formatting ───────────────────────────────────────────────────────────

  {
    schema: {
      name: "format_range",
      description: "Apply formatting to a cell range: bold, italic, fill color, font color, number format.",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Cell range to format.",
          },
          bold: { type: "boolean" },
          italic: { type: "boolean" },
          fillColor: {
            type: "string",
            description: "HTML color string, e.g. '#FF0000' or 'red'.",
          },
          fontColor: {
            type: "string",
            description: "HTML color string for text.",
          },
          numberFormat: {
            type: "string",
            description: "Excel number format string, e.g. '0.00' or 'mm/dd/yyyy'.",
          },
        },
        required: ["address"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = sheet.getRange(args.address as string);
        const fmt = range.format;

        if (args.bold !== undefined) fmt.font.bold = args.bold as boolean;
        if (args.italic !== undefined) fmt.font.italic = args.italic as boolean;
        if (args.fillColor) fmt.fill.color = args.fillColor as string;
        if (args.fontColor) fmt.font.color = args.fontColor as string;
        if (args.numberFormat) range.numberFormat = [[args.numberFormat as string]];

        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "autofit_columns",
      description: "Auto-fit column widths for the given range (or the whole used range if omitted).",
      parameters: {
        type: "object",
        properties: {
          sheet: {
            type: "string",
            description: "Sheet name. Defaults to the active sheet if omitted.",
          },
          address: {
            type: "string",
            description: "Range whose columns to auto-fit. If omitted, auto-fits the full used range.",
          },
        },
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = args.address
          ? sheet.getRange(args.address as string)
          : sheet.getUsedRange();
        range.format.autofitColumns();
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Sheet Management ─────────────────────────────────────────────────────

  {
    schema: {
      name: "create_sheet",
      description: "Add a new worksheet with the given name.",
      parameters: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: "Name for the new worksheet.",
          },
        },
        required: ["name"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.add(args.name as string);
        sheet.load("name,id,position");
        await context.sync();
        return { name: sheet.name, id: sheet.id, position: sheet.position };
      });
    },
  },

  {
    schema: {
      name: "rename_sheet",
      description: "Rename an existing worksheet.",
      parameters: {
        type: "object",
        properties: {
          oldName: { type: "string", description: "Current name of the worksheet." },
          newName: { type: "string", description: "New name for the worksheet." },
        },
        required: ["oldName", "newName"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(args.oldName as string);
        sheet.name = args.newName as string;
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "delete_sheet",
      description: "Delete a worksheet by name. This is permanent.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Name of the worksheet to delete." },
        },
        required: ["name"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(args.name as string);
        sheet.delete();
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "activate_sheet",
      description: "Make a worksheet the active (selected) sheet.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Name of the worksheet to activate." },
        },
        required: ["name"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(args.name as string);
        sheet.activate();
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Rows ─────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "manage_rows",
      description: "Insert or delete rows at a given position. Use action='insert' to add blank rows above the row index, or action='delete' to remove rows starting at the row index.",
      parameters: {
        type: "object",
        properties: {
          action: {
            type: "string",
            enum: ["insert", "delete"],
            description: "'insert' adds blank rows above rowIndex; 'delete' removes rows starting at rowIndex.",
          },
          sheet: { type: "string", description: "Sheet name. Defaults to the active sheet." },
          rowIndex: { type: "number", description: "0-based row index to insert before or delete from." },
          count: { type: "number", description: "Number of rows to insert or delete." },
        },
        required: ["action", "rowIndex", "count"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = sheet.getRangeByIndexes(args.rowIndex as number, 0, args.count as number, 1);
        if (args.action === "insert") {
          range.insert(Excel.InsertShiftDirection.down);
        } else {
          range.delete(Excel.DeleteShiftDirection.up);
        }
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Tables ───────────────────────────────────────────────────────────────

  {
    schema: {
      name: "create_table",
      description: "Convert a range into an Excel Table (ListObject).",
      parameters: {
        type: "object",
        properties: {
          sheet: { type: "string", description: "Sheet name. Defaults to the active sheet." },
          address: { type: "string", description: "Range address for the table, e.g. 'A1:D10'." },
          hasHeaders: {
            type: "boolean",
            description: "Whether the first row of the range contains headers. Defaults to true.",
          },
        },
        required: ["address"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const hasHeaders = args.hasHeaders !== false;
        const table = sheet.tables.add(args.address as string, hasHeaders);
        table.load("name,id");
        await context.sync();
        return { name: table.name, id: table.id };
      });
    },
  },

  // ─── Sort ─────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "sort_range",
      description: "Sort a range of cells by a specific column.",
      parameters: {
        type: "object",
        properties: {
          sheet: { type: "string", description: "Sheet name. Defaults to the active sheet." },
          address: { type: "string", description: "Range to sort, e.g. 'A1:D100'." },
          columnIndex: {
            type: "number",
            description: "0-based column index within the range to sort by.",
          },
          ascending: {
            type: "boolean",
            description: "Sort ascending if true, descending if false. Defaults to true.",
          },
        },
        required: ["address", "columnIndex"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const range = sheet.getRange(args.address as string);
        const ascending = args.ascending !== false;
        range.sort.apply([
          {
            key: args.columnIndex as number,
            ascending,
          },
        ]);
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Charts ───────────────────────────────────────────────────────────────

  {
    schema: {
      name: "add_chart",
      description: "Create a chart from a data range on the worksheet.",
      parameters: {
        type: "object",
        properties: {
          sheet: { type: "string", description: "Sheet name. Defaults to the active sheet." },
          dataAddress: {
            type: "string",
            description: "Range containing the chart data, e.g. 'A1:B10'.",
          },
          chartType: {
            type: "string",
            enum: [
              // Column
              "ColumnClustered", "ColumnStacked", "ColumnStacked100",
              "3DColumnClustered", "3DColumnStacked", "3DColumnStacked100", "3DColumn",
              "CylinderColClustered", "CylinderColStacked", "CylinderColStacked100", "CylinderCol",
              "ConeColClustered", "ConeColStacked", "ConeColStacked100", "ConeCol",
              "PyramidColClustered", "PyramidColStacked", "PyramidColStacked100", "PyramidCol",
              // Bar (horizontal)
              "BarClustered", "BarStacked", "BarStacked100",
              "3DBarClustered", "3DBarStacked", "3DBarStacked100",
              "CylinderBarClustered", "CylinderBarStacked", "CylinderBarStacked100",
              "ConeBarClustered", "ConeBarStacked", "ConeBarStacked100",
              "PyramidBarClustered", "PyramidBarStacked", "PyramidBarStacked100",
              // Line
              "Line", "3DLine",
              "LineStacked", "LineStacked100",
              "LineMarkers", "LineMarkersStacked", "LineMarkersStacked100",
              // Pie / Donut
              "Pie", "3DPie", "PieExploded", "3DPieExploded", "PieOfPie", "BarOfPie",
              "Doughnut", "DoughnutExploded",
              // Area
              "Area", "3DArea", "AreaStacked", "AreaStacked100", "3DAreaStacked", "3DAreaStacked100",
              // Scatter / Bubble
              "XYScatter", "XYScatterSmooth", "XYScatterSmoothNoMarkers", "XYScatterLines", "XYScatterLinesNoMarkers",
              "Bubble", "Bubble3DEffect",
              // Stock
              "StockHLC", "StockOHLC", "StockVHLC", "StockVOHLC",
              // Surface / Radar
              "Surface", "SurfaceWireframe", "SurfaceTopView", "SurfaceTopViewWireframe",
              "Radar", "RadarMarkers", "RadarFilled",
              // Modern / statistical
              "Histogram", "Boxwhisker", "Pareto", "Waterfall", "Funnel",
              "Treemap", "Sunburst", "RegionMap",
            ],
            description:
              "Chart type. Defaults to 'ColumnClustered'. " +
              "Columns/bars: ColumnClustered, ColumnStacked, ColumnStacked100, BarClustered, BarStacked, BarStacked100 (also 3D/Cylinder/Cone/Pyramid variants). " +
              "Line: Line, LineMarkers, LineStacked, LineMarkersStacked (and *100 variants). " +
              "Pie/Donut: Pie, 3DPie, PieExploded, PieOfPie, BarOfPie, Doughnut, DoughnutExploded. " +
              "Area: Area, AreaStacked, AreaStacked100 (also 3D variants). " +
              "Scatter/Bubble: XYScatter, XYScatterSmooth, XYScatterSmoothNoMarkers, XYScatterLines, XYScatterLinesNoMarkers, Bubble, Bubble3DEffect. " +
              "Stock: StockHLC (High-Low-Close), StockOHLC, StockVHLC, StockVOHLC. " +
              "Surface: Surface, SurfaceWireframe, SurfaceTopView, SurfaceTopViewWireframe. " +
              "Radar: Radar, RadarMarkers, RadarFilled. " +
              "Statistical/modern: Histogram, Boxwhisker, Pareto, Waterfall, Funnel, Treemap, Sunburst, RegionMap.",
          },
          title: {
            type: "string",
            description:
              "Optional chart title text. If omitted, the chart keeps Excel's default title.",
          },
          position: {
            type: "string",
            description:
              "Optional top-left cell anchor for the chart, e.g. 'D2'. If omitted, the chart is auto-placed clear of narrow data and stacked below any charts already on the sheet so they never overlap.",
          },
        },
        required: ["dataAddress"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const dataRange = sheet.getRange(args.dataAddress as string);
        const chartTypeName = (args.chartType as string) ?? "ColumnClustered";
        const validChartTypes = new Set<string>(Object.values(Excel.ChartType));
        const chartType = validChartTypes.has(chartTypeName)
          ? (chartTypeName as Excel.ChartType)
          : Excel.ChartType.columnClustered;

        // Read the existing charts' *actual* geometry up front so an auto-placed
        // new chart can drop below all of them. Counting charts isn't enough:
        // deletes/moves leave gaps, so a count×height slot can land on top of a
        // chart that's been repositioned (which is exactly how charts ended up
        // overlapping). Reading real positions makes non-overlap deterministic.
        const existingCharts = sheet.charts;
        existingCharts.load("items/top,items/height");
        await context.sync();

        const CHART_WIDTH = 360; // pt (Office default ~5in)
        const CHART_HEIGHT = 216; // pt (Office default ~3in)
        const CHART_GAP = 24; // pt of breathing room between stacked charts
        const CHART_LEFT = 320; // pt — clears narrow A:B-style data

        // Lowest occupied point across existing charts (0 when the sheet has none).
        const maxBottom = existingCharts.items.reduce(
          (lowest, c) => Math.max(lowest, c.top + c.height),
          0
        );

        const chart = sheet.charts.add(chartType, dataRange, Excel.ChartSeriesBy.auto);

        if (args.position) {
          // Caller chose where it goes — anchor the chart's top-left to that cell.
          chart.setPosition(args.position as string);
        } else {
          // Auto-place strictly below every existing chart so nothing overlaps.
          chart.left = CHART_LEFT;
          chart.top = maxBottom > 0 ? maxBottom + CHART_GAP : 0;
        }
        chart.width = CHART_WIDTH;
        chart.height = CHART_HEIGHT;

        if (args.title !== undefined) {
          chart.title.text = args.title as string;
          chart.title.visible = true;
        }

        chart.load("name");
        await context.sync();

        return {
          success: true,
          name: chart.name,
          chartType: chartTypeName,
          dataAddress: args.dataAddress as string,
        };
      });
    },
  },

  {
    schema: {
      name: "list_charts",
      description:
        "List the charts on a worksheet with their names and positions. Use this to find the name of a chart you want to retitle, move, or delete with format_chart.",
      parameters: {
        type: "object",
        properties: {
          sheet: { type: "string", description: "Sheet name. Defaults to the active sheet." },
        },
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const charts = sheet.charts;
        charts.load("items/name,items/top,items/left,items/width,items/height");
        await context.sync();

        return {
          charts: charts.items.map((c) => ({
            name: c.name,
            top: c.top,
            left: c.left,
            width: c.width,
            height: c.height,
          })),
        };
      });
    },
  },

  {
    schema: {
      name: "format_chart",
      description:
        "Restyle an existing chart (identify it by name from add_chart's result or list_charts). Every field is optional — set only what you want to change. Covers title, chart type, position, legend, data labels, the value (Y) and category (X) axes, and series colours. Use delete to remove the chart.",
      parameters: {
        type: "object",
        properties: {
          sheet: { type: "string", description: "Sheet name. Defaults to the active sheet." },
          name: { type: "string", description: "Name of the chart to update." },
          delete: {
            type: "boolean",
            description: "If true, delete the chart. All other fields are ignored.",
          },
          title: {
            type: "string",
            description: "Chart title text. Pass an empty string to hide the title.",
          },
          position: {
            type: "string",
            description: "Top-left cell anchor to move the chart to, e.g. 'A20'.",
          },
          chartType: {
            type: "string",
            description:
              "Change the chart type, e.g. 'Line', 'ColumnClustered', 'BarClustered', 'Pie', 'Area'. Uses the same type names as add_chart.",
          },
          legend: {
            type: "object",
            description: "Legend settings.",
            properties: {
              visible: { type: "boolean", description: "Show or hide the legend." },
              position: {
                type: "string",
                enum: ["Top", "Bottom", "Left", "Right", "Corner"],
                description: "Where the legend sits.",
              },
              overlay: { type: "boolean", description: "Overlay the legend on the plot area." },
            },
          },
          dataLabels: {
            type: "object",
            description: "Data-label settings (the numbers/text drawn on each data point).",
            properties: {
              visible: { type: "boolean", description: "Show or hide data labels." },
              showValue: { type: "boolean" },
              showCategoryName: { type: "boolean" },
              showSeriesName: { type: "boolean" },
              showPercentage: { type: "boolean", description: "Useful for pie/doughnut charts." },
              position: {
                type: "string",
                enum: ["Center", "InsideEnd", "InsideBase", "OutsideEnd", "BestFit"],
                description: "Where labels sit relative to each point.",
              },
              numberFormat: {
                type: "string",
                description: "Excel number format for the labels, e.g. '#,##0' or '0.0%'.",
              },
            },
          },
          valueAxis: {
            type: "object",
            description: "The value (Y) axis.",
            properties: {
              title: { type: "string", description: "Axis title text. Empty string hides it." },
              numberFormat: { type: "string", description: "Number format, e.g. '$#,##0'." },
              minimum: { type: "number" },
              maximum: { type: "number" },
              majorUnit: { type: "number", description: "Spacing between gridlines/ticks." },
              visible: { type: "boolean" },
            },
          },
          categoryAxis: {
            type: "object",
            description: "The category (X) axis.",
            properties: {
              title: { type: "string", description: "Axis title text. Empty string hides it." },
              numberFormat: { type: "string" },
              visible: { type: "boolean" },
            },
          },
          seriesColors: {
            type: "array",
            description:
              "Fill colours applied to the data series in plot order, e.g. ['#2E7D32'] makes the first series green. Each item is an HTML colour string.",
            items: { type: "string" },
          },
        },
        required: ["name"],
      },
    },
    handler: async (args) => {
      return Excel.run(async (context) => {
        const sheet = getSheet(context, args.sheet as string | undefined);
        const chart = sheet.charts.getItem(args.name as string);

        if (args.delete === true) {
          chart.delete();
          await context.sync();
          return { success: true, deleted: args.name as string };
        }

        // Series colours need the series count up front — getItemAt throws if we
        // overrun — so resolve it before applying everything else.
        const seriesColors = Array.isArray(args.seriesColors)
          ? (args.seriesColors as string[])
          : [];
        const dataLabels = args.dataLabels as Record<string, unknown> | undefined;
        // ChartDataLabels has no `visible` flag — labels are toggled per series
        // via series.hasDataLabels, so that path needs the series count too
        // (not just series colours).
        const toggleDataLabels = dataLabels?.visible !== undefined;
        let seriesCount = 0;
        if (seriesColors.length > 0 || toggleDataLabels) {
          chart.series.load("count");
          await context.sync();
          seriesCount = chart.series.count;
        }

        if (args.position) chart.setPosition(args.position as string);

        if (args.chartType) {
          const validChartTypes = new Set<string>(Object.values(Excel.ChartType));
          if (validChartTypes.has(args.chartType as string)) {
            chart.chartType = args.chartType as Excel.ChartType;
          }
        }

        if (args.title !== undefined) {
          const text = args.title as string;
          if (text === "") {
            chart.title.visible = false;
          } else {
            chart.title.text = text;
            chart.title.visible = true;
          }
        }

        const legend = args.legend as Record<string, unknown> | undefined;
        if (legend) {
          if (legend.visible !== undefined) chart.legend.visible = legend.visible as boolean;
          if (legend.position) {
            chart.legend.position = legend.position as Excel.ChartLegendPosition;
          }
          if (legend.overlay !== undefined) chart.legend.overlay = legend.overlay as boolean;
        }

        if (dataLabels) {
          if (toggleDataLabels) {
            const show = dataLabels.visible as boolean;
            for (let i = 0; i < seriesCount; i++) {
              chart.series.getItemAt(i).hasDataLabels = show;
            }
          }
          const dl = chart.dataLabels;
          if (dataLabels.showValue !== undefined) dl.showValue = dataLabels.showValue as boolean;
          if (dataLabels.showCategoryName !== undefined)
            dl.showCategoryName = dataLabels.showCategoryName as boolean;
          if (dataLabels.showSeriesName !== undefined)
            dl.showSeriesName = dataLabels.showSeriesName as boolean;
          if (dataLabels.showPercentage !== undefined)
            dl.showPercentage = dataLabels.showPercentage as boolean;
          if (dataLabels.position) {
            dl.position = dataLabels.position as Excel.ChartDataLabelPosition;
          }
          if (dataLabels.numberFormat) dl.numberFormat = dataLabels.numberFormat as string;
        }

        const valueAxis = args.valueAxis as Record<string, unknown> | undefined;
        if (valueAxis) {
          const ax = chart.axes.valueAxis;
          if (valueAxis.title !== undefined) {
            const t = valueAxis.title as string;
            if (t === "") ax.title.visible = false;
            else {
              ax.title.text = t;
              ax.title.visible = true;
            }
          }
          if (valueAxis.numberFormat) ax.numberFormat = valueAxis.numberFormat as string;
          if (valueAxis.minimum !== undefined) ax.minimum = valueAxis.minimum as number;
          if (valueAxis.maximum !== undefined) ax.maximum = valueAxis.maximum as number;
          if (valueAxis.majorUnit !== undefined) ax.majorUnit = valueAxis.majorUnit as number;
          if (valueAxis.visible !== undefined) ax.visible = valueAxis.visible as boolean;
        }

        const categoryAxis = args.categoryAxis as Record<string, unknown> | undefined;
        if (categoryAxis) {
          const ax = chart.axes.categoryAxis;
          if (categoryAxis.title !== undefined) {
            const t = categoryAxis.title as string;
            if (t === "") ax.title.visible = false;
            else {
              ax.title.text = t;
              ax.title.visible = true;
            }
          }
          if (categoryAxis.numberFormat) ax.numberFormat = categoryAxis.numberFormat as string;
          if (categoryAxis.visible !== undefined) ax.visible = categoryAxis.visible as boolean;
        }

        if (seriesColors.length > 0) {
          const limit = Math.min(seriesCount, seriesColors.length);
          for (let i = 0; i < limit; i++) {
            chart.series.getItemAt(i).format.fill.setSolidColor(seriesColors[i]);
          }
        }

        await context.sync();
        return { success: true, name: args.name as string };
      });
    },
  },
];

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const excelToolSchemas: ParentToolSchema[] = tools.map((t) => t.schema);

const handlerMap: Map<string, ExcelToolHandler> = new Map(
  tools.map((t) => [t.schema.name, t.handler])
);

// Tool calls route through the shared execution layer (@usable/office-embed
// tool-execution), which provides:
//   - requestId-keyed dedup for double-mount re-delivery (two message listeners
//     seeing the SAME TOOL_CALL — see the WHY comments there);
//   - serialization of Excel.run() contexts (Excel.js cannot service many
//     concurrent contexts; the embed dispatches a turn's calls all at once, so
//     without this a slow/hung call wedges the batch and they time out);
//   - per-call timeout + Office error → actionable-message mapping.
// Tools whose Excel.run() legitimately runs longer than the 15s
// DEFAULT_TOOL_TIMEOUT_MS and must not be killed mid-operation (Office.js has no
// cancellation — a timed-out write half-completes in the background, so a wrong
// cap reintroduces the diverged-state timeout it set out to fix):
//   - write_range: syncs per ≤10-row chunk (unbounded by row count)
//   - create_table: can carry a large initial data array
//   - add_chart: builds series/points in loops
// Mirrors PowerPoint's TOOL_TIMEOUT_OVERRIDES.
const TOOL_TIMEOUT_OVERRIDES: Map<string, number> = new Map([
  ["write_range", LONG_TOOL_TIMEOUT_MS],
  ["create_table", LONG_TOOL_TIMEOUT_MS],
  ["add_chart", LONG_TOOL_TIMEOUT_MS],
]);

export async function handleExcelToolCall(
  toolName: string,
  args: unknown,
  requestId?: string
): Promise<unknown> {
  const handler = handlerMap.get(toolName);
  if (!handler) {
    throw new Error(`Unknown Excel tool: "${toolName}"`);
  }

  return executeToolCall(
    toolName,
    args,
    () => handler(args as Record<string, unknown>),
    { requestId, timeoutMs: TOOL_TIMEOUT_OVERRIDES.get(toolName) }
  );
}
